import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  LabelList,
} from "recharts";
import type { ValueType, NameType } from "recharts/types/component/DefaultTooltipContent";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { getDataStats, DataStatsResponse } from "@/services/dataStatsApi";

type Labels = { complete: string; in_progress: string; title: string };

// Libellés de la complétude par entité (sémantique adaptée : scrapé vs résolu vs enrichi).
const ENTITY_LABELS: Record<string, Labels> = {
  candidates: { complete: "Enrichis", in_progress: "À enrichir", title: "Enrichissement" },
  companies: { complete: "Scrapées", in_progress: "En attente", title: "Scraping" },
  schools: { complete: "Scrapées", in_progress: "En attente", title: "Scraping" },
  languages: { complete: "Résolues", in_progress: "Non résolues", title: "Langues résolues" },
};

const C_OK = "hsl(var(--chart-1))";
const C_BAR = "hsl(var(--chart-2))";
const C_MUTED = "hsl(215 16% 78%)";

const fmt = (n: number) => n.toLocaleString("fr-FR");
const pct = (n: number, d: number) => (d ? `${Math.round((100 * n) / d)}%` : "—");

function Donut({
  title,
  data,
  colors,
  centerLabel,
  centerSub,
}: {
  title: string;
  data: { name: string; value: number }[];
  colors: string[];
  centerLabel: string;
  centerSub: string;
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="relative">
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={data} dataKey="value" nameKey="name" innerRadius={55} outerRadius={80} paddingAngle={2}>
                {data.map((_, i) => (
                  <Cell key={i} fill={colors[i % colors.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(v: ValueType, n: NameType) => [fmt(Number(v)), n]} />
            </PieChart>
          </ResponsiveContainer>
          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-2xl font-semibold">{centerLabel}</span>
            <span className="text-xs text-muted-foreground">{centerSub}</span>
          </div>
        </div>
        <div className="mt-2 flex flex-wrap justify-center gap-3 text-xs">
          {data.map((d, i) => (
            <span key={i} className="flex items-center gap-1">
              <span className="inline-block h-2 w-2 rounded-full" style={{ background: colors[i % colors.length] }} />
              {d.name}: {fmt(d.value)}
            </span>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Bloc de statistiques + graphiques pour une page admin "Données".
 * S'adapte à la forme renvoyée par /admin/data-stats :
 *  - complétude (complet/en cours) si l'entité en a une (langues/candidats/companies/schools)
 *  - couverture des liens candidats (entités avec complétude + liens)
 *  - concentration des liens (taxonomies sans complétude : top-10 vs traîne)
 *  - top-10 par candidats / abonnés
 * Non bloquant : en cas d'erreur, n'affiche rien.
 */
export function DataStats({ entity }: { entity: string }) {
  const navigate = useNavigate();
  const [s, setS] = useState<DataStatsResponse | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    setS(null);
    setFailed(false);
    getDataStats(navigate, entity)
      .then((d) => alive && setS(d))
      .catch(() => alive && setFailed(true));
    return () => {
      alive = false;
    };
  }, [navigate, entity]);

  if (failed) return null;
  if (!s) return <div className="text-sm text-gray-400">Chargement des statistiques…</div>;

  const labels = ENTITY_LABELS[entity] ?? { complete: "Complet", in_progress: "En cours", title: "Complétude" };
  const hasCompleteness = typeof s.complete === "number" && typeof s.in_progress === "number";
  const hasCoverage = typeof s.complete_links === "number" && typeof s.total_links === "number";
  const topSum = (s.top ?? []).reduce((a, t) => a + t.count, 0);
  const hasConcentration = !hasCompleteness && typeof s.total_links === "number" && (s.top?.length ?? 0) > 0;
  const topMetric = s.top_metric === "followers" ? "abonnés" : "candidats";
  const topData = (s.top ?? []).map((t) => ({ name: t.name, value: t.count })).reverse();

  return (
    <div className="grid gap-4 md:grid-cols-3">
      {hasCompleteness && (
        <Donut
          title={`${labels.title} · ${fmt(s.total)}`}
          data={[
            { name: labels.complete, value: s.complete! },
            { name: labels.in_progress, value: s.in_progress! },
          ]}
          colors={[C_OK, C_MUTED]}
          centerLabel={pct(s.complete!, s.total)}
          centerSub={labels.complete.toLowerCase()}
        />
      )}

      {hasCoverage && (
        <Donut
          title={`Couverture des liens · ${fmt(s.total_links!)}`}
          data={[
            { name: labels.complete, value: s.complete_links! },
            { name: labels.in_progress, value: s.total_links! - s.complete_links! },
          ]}
          colors={[C_OK, C_MUTED]}
          centerLabel={pct(s.complete_links!, s.total_links!)}
          centerSub="liens couverts"
        />
      )}

      {hasConcentration && (
        <Donut
          title={`Concentration des liens · ${fmt(s.total_links!)}`}
          data={[
            { name: "Top 10", value: topSum },
            { name: "Traîne", value: Math.max(0, s.total_links! - topSum) },
          ]}
          colors={[C_OK, C_MUTED]}
          centerLabel={pct(topSum, s.total_links!)}
          centerSub="sur le top 10"
        />
      )}

      {(s.top?.length ?? 0) > 0 && (
        <Card className={hasCompleteness || hasConcentration ? "" : "md:col-span-2"}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Top 10 par {topMetric}</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={topData} layout="vertical" margin={{ left: 8, right: 28, top: 4, bottom: 4 }}>
                <XAxis type="number" hide />
                <YAxis type="category" dataKey="name" width={130} tick={{ fontSize: 11 }} interval={0} />
                <Tooltip formatter={(v: ValueType) => [fmt(Number(v)), topMetric]} />
                <Bar dataKey="value" fill={C_BAR} radius={[0, 4, 4, 0]}>
                  <LabelList dataKey="value" position="right" formatter={(v: ValueType) => fmt(Number(v))} style={{ fontSize: 10 }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
